import {
  InvoiceDto,
  InvoicesConnection,
  InvoicesResponse,
  InvoiceStatus,
} from '../api';
import { Invoice } from '../Invoices.types';
import type { InvoicePaymentDetails } from '@/features/Profile/sections/Packages/components/InvoicePaymentModal';
import { capitalize, formatDate, formatNumber } from '@/utils/helpers';
import { DateTimeFormat } from '@/constants/common';

const EMPTY_INVOICES = { invoices: [], pageInfo: undefined };

export function capitalizeMonthInFormattedDate(formatted: string): string {
  return formatted.replace(
    /^(\d+\s)(\S+)/,
    (_match, dayAndSpace: string, month: string) =>
      `${dayAndSpace}${capitalize(month)}`
  );
}

function formatStatusDateDetails(statusDatetime: string): string {
  return capitalizeMonthInFormattedDate(
    formatDate(statusDatetime, DateTimeFormat.longMonthWithYearAndTime)
  );
}

function formatPeriod(
  startsAt: string | null,
  expiresAt: string | null
): string | undefined {
  if (!startsAt || !expiresAt) {
    return undefined;
  }

  return `${formatDate(startsAt, 'dd.MM.yyyy')} — ${formatDate(
    expiresAt,
    'dd.MM.yyyy'
  )}`;
}

export function mapInvoiceDtoToInvoice(dto: InvoiceDto): Invoice {
  return {
    id: dto.id,
    number: dto.legacyResourceId,
    service: dto.service ?? '',
    amount: dto.amount,
    formattedAmount: `${formatNumber(dto.amount, 2)} ₼`,
    status: dto.status,
    createdDate: formatDate(dto.invoiceDate, 'dd.MM.yyyy'),
    statusDate: formatDate(dto.statusDatetime, DateTimeFormat.fullTime),
    statusDateDetails: formatStatusDateDetails(dto.statusDatetime),
    paymentSource: dto.paymentSource ?? undefined,
    printUrl: dto.printUrl ?? undefined,
    discountPercent: dto.discount ?? undefined,
    formattedVatAmount: `${formatNumber(dto.vatAmount, 2)} AZN`,
    activationDate: dto.startsAt
      ? formatDate(dto.startsAt, DateTimeFormat.date)
      : undefined,
    periodDays: dto.period ?? undefined,
    period: formatPeriod(dto.startsAt, dto.expiresAt),
    paidAdsCount: dto.paidAdsCount ?? undefined,
    walletAmount: dto.walletAmount ?? undefined,
    customerName: dto.customerName ?? undefined,
    customerTin: dto.customerTin ?? undefined,
    contractNumber: dto.contractNumber ?? undefined,
    canDownload: !!dto.printUrl,
    canPay: dto.status === InvoiceStatus.Unpaid,
  };
}

export function getInvoicePaymentDetails(
  invoice: Invoice
): InvoicePaymentDetails {
  return {
    activationDate: invoice.activationDate ?? null,
    benefitPercent: null,
    originalPrice: null,
    packageName: invoice.service,
    periodDays: invoice.periodDays ?? null,
    price: invoice.amount,
    promoPrice: null,
    promotionDiscountAmount: null,
    promotionDiscountPercent: null,
    showPromotionLabel: false,
    showPromoPriceLabel: false,
  };
}

export function mapInvoicesConnection(connection: InvoicesConnection) {
  return {
    invoices: connection.nodes.map(mapInvoiceDtoToInvoice),
    pageInfo: connection.pageInfo,
  };
}

export function getInvoicesAndPageInfo(data: InvoicesResponse | undefined) {
  if (!data) {
    return EMPTY_INVOICES;
  }

  return mapInvoicesConnection(data.currentUserInvoices);
}

function mergeNodes(
  previous: InvoiceDto[],
  incoming: InvoiceDto[]
): InvoiceDto[] {
  const existingIds = new Set(previous.map(node => node.id));
  return [...previous, ...incoming.filter(node => !existingIds.has(node.id))];
}

export function mergeInvoicesResults(
  prevResult: InvoicesResponse,
  { fetchMoreResult }: { fetchMoreResult?: InvoicesResponse }
): InvoicesResponse {
  if (!fetchMoreResult) {
    return prevResult;
  }

  return {
    currentUserInvoices: {
      ...fetchMoreResult.currentUserInvoices,
      nodes: mergeNodes(
        prevResult.currentUserInvoices.nodes,
        fetchMoreResult.currentUserInvoices.nodes
      ),
    },
  };
}
